import { FormEvent, useEffect, useState } from "react";
import { Download, RefreshCw, Search, Trash2, UserCircle2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import {
  AdminNewsletterSubscriber,
  AdminUser,
  PaginationMeta,
  deleteAdminNewsletterSubscriber,
  exportAdminNewsletterSubscribers,
  getCurrentAdmin,
  listAdminNewsletterSubscribers,
  logout,
} from "./api";
import { useConfirmDialog } from "./components/ConfirmDialog";
import Pagination from "./components/Pagination";

const defaultPagination: PaginationMeta = { Page: 1, PerPage: 20, Total: 0, TotalPages: 1 };

export default function AdminNewsletterSubscribers() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [items, setItems] = useState<AdminNewsletterSubscriber[]>([]);
  const [counts, setCounts] = useState({ Active: 0, Inactive: 0 });
  const [pagination, setPagination] = useState<PaginationMeta>(defaultPagination);
  const [status, setStatus] = useState("");
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(true);
  const [pageMessage, setPageMessage] = useState("");
  const [exporting, setExporting] = useState(false);
  const { confirm, ConfirmDialog } = useConfirmDialog();

  async function load(filters = { Status: status, Keyword: keyword, Page: pagination.Page, PerPage: pagination.PerPage }) {
    setLoading(true);
    setPageMessage("");
    try {
      const me = await getCurrentAdmin();
      setCurrentUser(me.User);
      const data = await listAdminNewsletterSubscribers(filters);
      setItems(data.Items);
      setCounts(data.Counts || { Active: 0, Inactive: 0 });
      setPagination(data.Pagination || defaultPagination);
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "訂閱名單載入失敗。");
      navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load({ Status: "", Keyword: "", Page: 1, PerPage: pagination.PerPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    load({ Status: status, Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage });
  }

  async function handleExport() {
    setExporting(true);
    setPageMessage("");
    try {
      await exportAdminNewsletterSubscribers();
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "匯出失敗，請稍後再試。");
    } finally {
      setExporting(false);
    }
  }

  async function handleDelete(item: AdminNewsletterSubscriber) {
    const confirmed = await confirm({
      title: "刪除訂閱資料？",
      description: (
        <>
          確定要刪除 <strong className="font-bold text-[#203d32]">{item.Email}</strong> 的訂閱紀錄嗎？此操作無法復原。
        </>
      ),
      confirmText: "刪除資料",
      cancelText: "取消",
      tone: "danger",
    });
    if (!confirmed) return;

    try {
      await deleteAdminNewsletterSubscriber(item.NewsletterSubscriberId);
      await load({ Status: status, Keyword: keyword.trim(), Page: pagination.Page, PerPage: pagination.PerPage });
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "刪除失敗，請稍後再試。");
    }
  }

  const allItemCount = counts.Active + counts.Inactive || pagination.Total;

  return (
    <AdminLayout sectionLabel="內容管理" title="電子報訂閱名單" user={currentUser} onLogout={handleLogout}>
      <div className="mb-4 grid gap-3 md:grid-cols-3">
        <button className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3 text-left hover:border-[#b8c8bf]" onClick={() => load({ Status: "", Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage })} type="button">
          <p className="text-xs font-bold text-[#7b877f]">全部名單</p>
          <p className="mt-1 text-2xl font-bold text-[#203d32]">{allItemCount}</p>
        </button>
        <button className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3 text-left hover:border-[#b8c8bf]" onClick={() => load({ Status: "Active", Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage })} type="button">
          <p className="text-xs font-bold text-[#7b877f]">訂閱中</p>
          <p className="mt-1 text-2xl font-bold text-[#245438]">{counts.Active}</p>
        </button>
        <button className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3 text-left hover:border-[#b8c8bf]" onClick={() => load({ Status: "Inactive", Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage })} type="button">
          <p className="text-xs font-bold text-[#7b877f]">已取消訂閱</p>
          <p className="mt-1 text-2xl font-bold text-[#59665f]">{counts.Inactive}</p>
        </button>
      </div>

      <form className="mb-5 grid gap-3 rounded-md border border-[#dfe6e1] bg-white p-3 md:sticky md:top-[102px] md:z-10 md:flex md:flex-wrap md:items-end md:bg-white/95 md:p-4 md:shadow-[0_10px_24px_rgba(32,61,50,.08)] md:backdrop-blur" onSubmit={handleSearch}>
        <div className="grid min-w-0 gap-1 md:min-w-[120px]">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="newsletter-status">狀態</label>
          <select id="newsletter-status" className="h-11 rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">全部</option>
            <option value="Active">訂閱中</option>
            <option value="Inactive">已取消</option>
          </select>
        </div>
        <div className="grid min-w-0 gap-1 md:min-w-[260px] md:flex-1">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="newsletter-keyword">關鍵字</label>
          <input id="newsletter-keyword" className="h-11 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="Email" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
        </div>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32]">
          <Search size={18} />
          搜尋
        </button>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => load({ Status: status, Keyword: keyword.trim(), Page: pagination.Page, PerPage: pagination.PerPage })} type="button">
          <RefreshCw size={18} />
          重新整理
        </button>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32] disabled:cursor-not-allowed disabled:opacity-60" disabled={exporting} onClick={handleExport} type="button">
          <Download size={18} />
          {exporting ? "匯出中" : "匯出 Excel"}
        </button>
      </form>

      {pageMessage && <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{pageMessage}</div>}

      <div className="rounded-md border border-[#dfe6e1] bg-white">
        <div className="max-h-[62vh] overflow-auto">
          <table className="w-full min-w-[720px] table-fixed border-collapse text-left">
            <thead className="sticky top-0 z-10 bg-[#f0f3f1] text-sm text-[#59665f] shadow-[inset_0_-1px_0_#dfe6e1]">
              <tr>
                <th className="w-[300px] px-4 py-3">Email</th>
                <th className="w-[160px] px-4 py-3">關聯會員</th>
                <th className="w-[100px] px-4 py-3">狀態</th>
                <th className="w-[150px] px-4 py-3">訂閱時間</th>
                <th className="w-[110px] px-4 py-3">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={5}>載入中...</td></tr>
              ) : items.length === 0 ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={5}>目前沒有符合條件的訂閱資料。</td></tr>
              ) : items.map((item) => (
                <tr className="border-t border-[#edf0ee]" key={item.NewsletterSubscriberId}>
                  <td className="px-4 py-4 font-bold text-[#203d32]">{item.Email}</td>
                  <td className="px-4 py-4 text-sm">
                    {item.Member ? (
                      <Link className="inline-flex items-center gap-1.5 font-bold text-[#245438] hover:underline" to={`/admin/members/${item.Member.MemberId}`}>
                        <UserCircle2 size={16} />
                        {item.Member.Name}
                      </Link>
                    ) : (
                      <span className="text-[#9a8a7a]">非會員</span>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${item.IsActive ? "bg-[#e9f4ec] text-[#245438]" : "bg-[#eeeeee] text-[#59635d]"}`}>{item.IsActive ? "訂閱中" : "已取消"}</span>
                  </td>
                  <td className="px-4 py-4 text-sm text-[#59665f]">{item.SubscribedAt || "-"}</td>
                  <td className="px-4 py-4">
                    <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#f2b8b5] bg-white px-3 font-bold text-[#b3261e] transition hover:bg-[#fdebea]" onClick={() => handleDelete(item)} type="button">
                      <Trash2 size={16} />
                      刪除
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination disabled={loading} pagination={pagination} onChange={(page) => load({ Status: status, Keyword: keyword.trim(), Page: page, PerPage: pagination.PerPage })} />
      </div>

      <ConfirmDialog />
    </AdminLayout>
  );
}
